import Vue from 'vue';
import VueRouter from 'vue-router';
import BootstrapVue from 'bootstrap-vue';
import { ValidationProvider, ValidationObserver, extend, localize } from 'vee-validate';
import en from 'vee-validate/dist/locale/en.json';
import * as rules from 'vee-validate/dist/rules';
import App from './App.vue';
import router from './router';
import axios from 'axios';
import store from './store';
import eventBus from './event-bus';
import 'bootstrap/dist/css/bootstrap.css';
import 'bootstrap-vue/dist/bootstrap-vue.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import '@fortawesome/fontawesome-free/css/all.css';
import GSignInButton from 'vue-google-signin-button'

Vue.use(GSignInButton);
Vue.use(VueRouter);
Vue.use(BootstrapVue, {
    BOverlay: {
        zIndex: 2000
    }
});

import { installPopperZoomFix } from './popperZoomFix';
installPopperZoomFix();

import { getAppEnvironment, getLogoUrl, getLogoColor, setEnvironmentFavicons } from './envHelper';
setEnvironmentFavicons();
Vue.prototype.$appEnv = getAppEnvironment();
Vue.prototype.$logoUrl = getLogoUrl();
Vue.prototype.$logoColor = getLogoColor();

// Register VeeValidate components globally
Vue.component('ValidationProvider', ValidationProvider);
Vue.component('ValidationObserver', ValidationObserver);

import LoadSpinner from './components/load-spinner.vue';
Vue.component('load-spinner', LoadSpinner);
// Add VeeValidate rules
Object.keys(rules).forEach((rule) => {
    extend(rule, rules[rule]);
});
//Locale of rule messages
localize('en', en);

// Create an Axios instance with a base URL
// Set the instance as a prototype for easy access in components
let rawApiUri = process.env.VUE_APP_API_URI || "beta.velocytrack.dev:44888";

// Dynamically align API hostname when accessed locally via .dev, .local, or localhost
if (typeof window !== 'undefined' && window.location && window.location.hostname) {
    const hostname = window.location.hostname;
    if (hostname.endsWith('.dev') || hostname.endsWith('.local') || hostname === 'localhost' || hostname === '127.0.0.1') {
        const port = rawApiUri.includes(':') ? rawApiUri.split(':')[1] : '44888';
        rawApiUri = `${hostname}:${port}`;
    }
}

const uri = rawApiUri.startsWith('http://') || rawApiUri.startsWith('https://')
    ? rawApiUri
    : `https://${rawApiUri}`;

const axiosInstance = axios.create({
    baseURL: uri,
    withCredentials: true
});

axiosInstance.interceptors.request.use((config) => {
    try {
        const selectedProjId = store?.state?.selectedProjectId || localStorage.getItem('velocytrack_last_project_id');
        if (selectedProjId && !config.headers['X-Project-Id'] && !config.headers['x-project-id']) {
            config.headers['X-Project-Id'] = selectedProjId;
        }
    } catch (e) {
        // ignore storage errors
    }
    return config;
}, (error) => Promise.reject(error));


// Global 5xx error response interceptor: emit safe error event for user-facing toast with report action
axiosInstance.interceptors.response.use(
    (response) => response,
    (error) => {
        if (axios.isCancel(error) || error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError' || error?.name === 'AbortError' || error?.message === 'canceled' || !!error?.__CANCEL__) {
            return Promise.reject(error);
        }
        if (error?.response?.data?.isPermissionError && !error?.config?.skipGlobalPermissionToast) {
            eventBus.$emit('ado-permission-error', error.response.data);
            return Promise.reject(error);
        }
        const status = error?.response?.status;
        if (status >= 500) {
            const endpoint = error?.config?.url || 'API';
            const method = error?.config?.method ? error.config.method.toUpperCase() : 'GET';
            const serverMessage = error?.response?.data?.title || error?.response?.data?.message || (typeof error?.response?.data === 'string' ? error.response.data : error?.message) || 'Internal Server Error';
            eventBus.$emit('http-5xx-error', {
                status,
                endpoint,
                method,
                serverMessage,
                timestamp: new Date().toISOString()
            });
        }
        return Promise.reject(error);
    }
);

Vue.prototype.$http = axiosInstance

// Suppress unhandled rejections and runtime error overlays caused by network reachability or request cancellations
if (typeof window !== 'undefined') {
    const isCancelOrOffline = (err) => {
        if (!err) return false;
        if (axios.isCancel(err) || err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError' || err?.name === 'AbortError' || err?.message === 'canceled' || !!err?.__CANCEL__) {
            return true;
        }
        if (err.isNetworkError || err.status === 0) return true;
        const msg = String(err.message || err || '');
        return msg.includes('Service host is unreachable') || msg.includes('Network Error');
    };

    window.addEventListener('unhandledrejection', (event) => {
        if (isCancelOrOffline(event.reason) || store?.state?.isServiceUnavailable) {
            event.stopImmediatePropagation();
            event.preventDefault();
        }
    }, true);

    window.addEventListener('error', (event) => {
        if (isCancelOrOffline(event.error) || store?.state?.isServiceUnavailable) {
            event.stopImmediatePropagation();
            event.preventDefault();
        }
    }, true);
}

Vue.config.errorHandler = (err) => {
    if (axios.isCancel(err) || err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError' || err?.name === 'AbortError' || err?.message === 'canceled' || !!err?.__CANCEL__) {
        return;
    }
    if (err?.status === 402 || err?.response?.status === 402 || err?.isPlanUpgrade) {
        return;
    }
    if (
        err?.isNetworkError ||
        err?.status === 0 ||
        err?.message?.includes('Service host is unreachable') ||
        err?.message?.includes('Network Error') ||
        store?.state?.isServiceUnavailable
    ) {
        return;
    }
    console.error(err);
};

const app = new Vue({
    router,
    store,
    eventBus,
    render: h => h(App)
});

if (process.env.VUE_APP_USE_DEV_DETAILS) {
    Vue.config.productionTip = false;
    Vue.config.devtools = true;
    Vue.config.debug = true;
    Vue.config.silent = false;
}

try {
    const initialTheme = store?.state?.theme || 'dark';
    document.documentElement.setAttribute('data-theme', initialTheme);
} catch (e) {
    // ignore
}

app.$mount('#app');
